import React, { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';
import { getLogoUrl } from '@/utils/logoUtils';

interface InstitutionSelectionStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

export const InstitutionSelectionStep: React.FC<InstitutionSelectionStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
}) => {
  const [institutions, setInstitutions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInstitutions();
  }, []);

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const data = await agentApi.getApprovedInstitutions();
      setInstitutions(data || []);
    } catch (error) {
      console.error('Error loading institutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
    // Clear program selection when institution changes
    if (field === 'universityId' && value !== formData.universityId) {
      updateFormData({ universityProgramId: undefined });
    }
  };

  const filteredInstitutions = institutions.filter(inst =>
    inst.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <h5 className="fw-bold mb-3">Select Institution</h5>
      <p className="text-muted mb-3">
        Please select the institution you would like to apply to from your approved institutions list.
      </p>

      {loading ? (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading institutions...</span>
          </div>
        </div>
      ) : institutions.length === 0 ? (
        <div className="alert alert-warning">
          <strong>No approved institutions found.</strong>
          <p className="mb-0">Please contact your administrator to get approved for institutions.</p>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="mb-3">
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search institutions by name, country, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
              <i className="bi bi-search position-absolute end-0 top-50 translate-middle-y me-3 text-muted small"></i>
            </div>
          </div>

          {/* Institution Selection */}
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Institution <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${errors.universityId ? 'is-invalid' : ''}`}
              value={formData.universityId || ''}
              onChange={(e) => handleChange('universityId', e.target.value ? parseInt(e.target.value) : undefined)}
            >
              <option value="">Select an institution</option>
              {filteredInstitutions.map(inst => (
                <option key={inst.id} value={inst.id}>
                  {inst.name} - {inst.city}, {inst.country}
                </option>
              ))}
            </select>
            {errors.universityId && (
              <div className="invalid-feedback d-block">{errors.universityId}</div>
            )}
          </div>

          {/* Selected Institution Info */}
          {formData.universityId && (
            <div className="card border-primary mb-3">
              <div className="card-body">
                {(() => {
                  const selected = institutions.find(inst => inst.id === formData.universityId);
                  if (!selected) return null;
                  return (
                    <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start">
                      {getLogoUrl(selected.logo) && (
                        <img
                          src={getLogoUrl(selected.logo) || ''}
                          alt={selected.name}
                          className="me-sm-3 mb-2 mb-sm-0"
                          style={{ width: '60px', height: '60px', objectFit: 'contain', flexShrink: 0 }}
                          onError={(e) => {
                            // Hide broken image
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="text-center text-sm-start flex-grow-1">
                        <h6 className="mb-1 fw-bold">{selected.name}</h6>
                        <div className="d-flex align-items-center justify-content-center justify-content-sm-start text-muted small">
                          <Globe size={14} className="me-1" />
                          <span>{selected.city}, {selected.country}</span>
                        </div>
                        {selected.commissionRate && (
                          <div className="small text-muted mt-1">
                            Commission Rate: {(selected.commissionRate * 100).toFixed(1)}%
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Institution List (Alternative View) */}
          {filteredInstitutions.length > 0 && (
            <div className="mt-4">
              <h6 className="fw-semibold mb-3">Available Institutions ({filteredInstitutions.length})</h6>
              <div className="row g-3">
                {filteredInstitutions.slice(0, 12).map(inst => (
                  <div key={inst.id} className="col-6 col-md-4 col-lg-3 col-xl-2">
                    <div
                      className={`card h-100 border ${formData.universityId === inst.id ? 'border-primary bg-light' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleChange('universityId', inst.id)}
                    >
                      <div className="card-body p-2">
                        {getLogoUrl(inst.logo) && (
                          <div className="text-center mb-2">
                            <img
                              src={getLogoUrl(inst.logo) || ''}
                              alt={inst.name}
                              style={{ maxWidth: '100%', maxHeight: '50px', objectFit: 'contain' }}
                              onError={(e) => {
                                // Hide broken image
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <h6 className="card-title mb-1 small fw-semibold text-center" style={{ fontSize: '0.75rem', lineHeight: '1.2', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {inst.name}
                        </h6>
                        <p className="card-text small text-muted mb-0 text-center" style={{ fontSize: '0.7rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          <Globe size={12} className="me-1" />
                          {inst.city}, {inst.country}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredInstitutions.length > 12 && (
                <p className="text-muted small mt-3">
                  Showing first 12 institutions. Use the search to find more.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

